import {
  bulkPriceSchema,
  PRICE_ROUNDING_LABELS,
  PRICE_ROUNDINGS,
  type BulkPriceInput,
  type BulkPriceMode,
  type BulkPricePreview,
  type CategoryDTO,
  type PriceRounding,
} from '@bifeai/shared';
import { Button, Callout, Dialog, EmptyState, Field, Input, Select } from '@bifeai/ui';
import { Percent } from 'lucide-react';
import { useMemo, useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useCatalogAccess, useProducts } from '../../lib/catalog';
import { formatMoney } from '../../lib/format';
import { useSession } from '../../lib/session';
import { parseDecimal } from '../../lib/units';

type What = 'all' | 'categories' | 'products';
type Where = 'all' | 'some';

const box: React.CSSProperties = {
  maxHeight: 200,
  overflow: 'auto',
  border: '1px solid var(--bf-line)',
  borderRadius: 'var(--bf-radius-md)',
  padding: 8,
  display: 'grid',
  gap: 6,
};

/**
 * Aumento (o baja) masivo con vista previa: por porcentaje o monto fijo, en todos los productos, en algunas categorías
 * o en productos sueltos; y, para la central, en todas las sucursales propias (también las que tienen precio propio)
 * o solo en las que se tilden.
 */
export function BulkPriceDialog({
  categories,
  onClose,
  onApplied,
}: {
  categories: CategoryDTO[];
  onClose: () => void;
  onApplied: (affected: number) => Promise<void>;
}) {
  const access = useCatalogAccess();
  const { me } = useSession();
  const ownBranches = me.branches.filter((b) => !b.franchise);
  const central = access.canGeneralPrice;
  const [mode, setMode] = useState<BulkPriceMode>('PERCENT');
  const [value, setValue] = useState('');
  const [rounding, setRounding] = useState<PriceRounding>('HUNDRED');
  const [what, setWhat] = useState<What>('all');
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [productIds, setProductIds] = useState<string[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [where, setWhere] = useState<Where>('all');
  const [includeOverrides, setIncludeOverrides] = useState(true);
  const [branchIds, setBranchIds] = useState<string[]>(access.priceBranchId && central ? [access.priceBranchId] : []);
  const [preview, setPreview] = useState<BulkPricePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'preview' | 'apply' | null>(null);
  const products = useProducts();
  const shownProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    return (products.data ?? []).filter((p) => !q || p.name.toLowerCase().includes(q)).slice(0, 150);
  }, [products.data, productSearch]);

  const reset = () => setPreview(null);
  const toggle = (list: string[], id: string, on: boolean) => (on ? [...list, id] : list.filter((x) => x !== id));
  const num = parseDecimal(value) ?? 0;

  function input(): BulkPriceInput | null {
    if (what === 'categories' && categoryIds.length === 0) return fail('Elegí al menos una categoría.');
    if (what === 'products' && productIds.length === 0) return fail('Elegí al menos un producto.');
    if (central && where === 'some' && branchIds.length === 0) return fail('Elegí al menos una sucursal.');
    const parsed = bulkPriceSchema.safeParse({
      mode,
      percent: mode === 'PERCENT' ? parseDecimal(value) : 0,
      amountCents: mode === 'AMOUNT' ? Math.round((parseDecimal(value) ?? Number.NaN) * 100) : 0,
      categoryIds: what === 'categories' ? categoryIds : [],
      productIds: what === 'products' ? productIds : [],
      // Una sucursal (o franquicia) cambia solo lo suyo.
      branchIds: !central ? (access.priceBranchId ? [access.priceBranchId] : []) : where === 'some' ? branchIds : [],
      includeOverrides: central && where === 'all' && includeOverrides,
      rounding,
    });
    if (!parsed.success) return fail(parsed.error.issues[0]?.message ?? 'Revisá el valor.');
    return parsed.data;
  }
  function fail(msg: string): null {
    setError(msg);
    return null;
  }

  async function run(kind: 'preview' | 'apply') {
    const body = input();
    if (!body) return;
    setError(null);
    setBusy(kind);
    try {
      const res = await api.post<BulkPricePreview>(`/prices/bulk/${kind}`, body);
      if (kind === 'apply') await onApplied(res.affected);
      else setPreview(res);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  const byBranch = preview?.rows.some((r) => r.branchId !== null) ?? false;
  const products_ = new Set(preview?.rows.map((r) => r.productId)).size;

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title="Aumento masivo de precios"
      description="Primero ves cómo quedan los precios. Nada cambia hasta que confirmás."
      wide
    >
      <div className="bf-form">
        {error && <Callout tone="danger">{error}</Callout>}

        <div className="bf-form-row">
          <Field label="Cómo">
            <Select
              value={mode}
              onChange={(e) => {
                setMode(e.target.value as BulkPriceMode);
                reset();
              }}
            >
              <option value="PERCENT">Por porcentaje (%)</option>
              <option value="AMOUNT">Por monto fijo ($)</option>
            </Select>
          </Field>
          <Field
            label={mode === 'PERCENT' ? 'Porcentaje' : 'Monto a sumar ($)'}
            hint={mode === 'PERCENT' ? 'Para bajar, un número negativo (ej. -5).' : 'Se suma a cada precio. Para bajar, negativo (ej. -500).'}
          >
            <Input
              inputMode="decimal"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                reset();
              }}
              placeholder={mode === 'PERCENT' ? '10' : '500'}
              autoFocus
            />
          </Field>
          <Field label="Redondeo">
            <Select
              value={rounding}
              onChange={(e) => {
                setRounding(e.target.value as PriceRounding);
                reset();
              }}
            >
              {PRICE_ROUNDINGS.map((r) => (
                <option key={r} value={r}>
                  {PRICE_ROUNDING_LABELS[r]}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="Qué productos">
          <Select
            value={what}
            onChange={(e) => {
              setWhat(e.target.value as What);
              reset();
            }}
          >
            <option value="all">Todos los productos</option>
            <option value="categories">Algunas categorías</option>
            <option value="products">Productos sueltos</option>
          </Select>
        </Field>
        {what === 'categories' && (
          <div style={box}>
            {categories.map((c) => (
              <label key={c.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={categoryIds.includes(c.id)}
                  onChange={(e) => {
                    setCategoryIds((l) => toggle(l, c.id, e.target.checked));
                    reset();
                  }}
                />
                {c.name}
              </label>
            ))}
          </div>
        )}
        {what === 'products' && (
          <>
            <Input
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              placeholder="Buscar producto"
              aria-label="Buscar producto"
            />
            <div style={box}>
              {shownProducts.map((p) => (
                <label key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={productIds.includes(p.id)}
                    onChange={(e) => {
                      setProductIds((l) => toggle(l, p.id, e.target.checked));
                      reset();
                    }}
                  />
                  {p.name}
                </label>
              ))}
            </div>
            {productIds.length > 0 && <p className="bf-cell-sub">{productIds.length} elegido(s).</p>}
          </>
        )}

        {central && ownBranches.length > 1 ? (
          <>
            <p className="bf-label" style={{ margin: 0 }}>
              Dónde
            </p>
            <div className="bf-radio-list">
              <label className="bf-radio">
                <input
                  type="radio"
                  checked={where === 'all'}
                  onChange={() => {
                    setWhere('all');
                    reset();
                  }}
                />
                <span>
                  Todas las sucursales
                  <small>Cambia el precio general. Las franquicias que usan los precios de la marca lo siguen solas.</small>
                </span>
              </label>
              {where === 'all' && (
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', paddingLeft: 14 }}>
                  <input
                    type="checkbox"
                    checked={includeOverrides}
                    onChange={(e) => {
                      setIncludeOverrides(e.target.checked);
                      reset();
                    }}
                  />
                  También las sucursales que tienen precio propio
                </label>
              )}
              <label className="bf-radio">
                <input
                  type="radio"
                  checked={where === 'some'}
                  onChange={() => {
                    setWhere('some');
                    reset();
                  }}
                />
                <span>
                  Solo algunas sucursales
                  <small>Parte del precio vigente en cada una y lo deja como precio propio de esa sucursal.</small>
                </span>
              </label>
              {where === 'some' && (
                <div style={{ ...box, marginLeft: 14 }}>
                  {ownBranches.map((b) => (
                    <label key={b.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={branchIds.includes(b.id)}
                        onChange={(e) => {
                          setBranchIds((l) => toggle(l, b.id, e.target.checked));
                          reset();
                        }}
                      />
                      {b.name}
                    </label>
                  ))}
                </div>
              )}
            </div>
            {me.branches.some((b) => b.franchise) && (
              <p className="bf-cell-sub" style={{ margin: 0 }}>
                Las franquicias con precios propios o con margen manejan los suyos: este aumento no los toca.
              </p>
            )}
          </>
        ) : (
          !central && (
            <Callout>
              Se aplica solo a <strong>{access.priceBranchName}</strong>.
            </Callout>
          )
        )}

        {preview &&
          (preview.affected === 0 ? (
            <EmptyState icon={<Percent />} title="No hay precios para cambiar" text="Revisá lo que elegiste o cargá precios primero." />
          ) : (
            <>
              <p>
                <strong>{preview.affected}</strong> {preview.affected === 1 ? 'precio cambia' : 'precios cambian'}
                {byBranch ? ` (${products_} ${products_ === 1 ? 'producto' : 'productos'})` : ''}.
              </p>
              <div className="bf-scroll-box">
                <table className="bf-table">
                  <thead>
                    <tr>
                      <th>Producto</th>
                      {byBranch && <th>Dónde</th>}
                      <th style={{ textAlign: 'right' }}>Hoy</th>
                      <th style={{ textAlign: 'right' }}>Nuevo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r) => (
                      <tr key={`${r.productId}-${r.branchId ?? 'general'}`}>
                        <td className="bf-cell-main">{r.name}</td>
                        {byBranch && <td className="bf-text-muted">{r.branchName ?? 'General'}</td>}
                        <td className="bf-num bf-text-muted" style={{ textAlign: 'right' }}>
                          {formatMoney(r.oldPriceCents)}
                        </td>
                        <td className="bf-num" style={{ textAlign: 'right', fontWeight: 600 }}>
                          {formatMoney(r.newPriceCents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ))}

        <div className="bf-form-actions">
          <Button onClick={onClose} disabled={Boolean(busy)}>
            Cancelar
          </Button>
          {preview && preview.affected > 0 ? (
            <Button variant="primary" loading={busy === 'apply'} onClick={() => void run('apply')}>
              {num >= 0 ? 'Aplicar aumento' : 'Aplicar baja'} a {preview.affected}
            </Button>
          ) : (
            <Button variant="primary" loading={busy === 'preview'} onClick={() => void run('preview')}>
              Ver cómo quedan
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
}
